// Audit log page (Phase 5.6). Read-only table of the 100 most recent events
// for the tenant, optionally filtered by action namespace.
//
// The filter is a single <select> over a known-good set of namespaces (those
// the writers actually emit — see master plan §6 / §11-A03). The API accepts
// any prefix, so the fallback "Custom…" path lets the user type one in when
// the writer set grows without blocking on a UI release.
//
// Each row is clickable to expand the `meta` JSON inline. We avoid rendering
// every meta blob by default — some payloads (stripe event objects) are wide
// enough to force the table into horizontal scroll.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import type { AuditLogEntry } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError } from '../../lib/api-client.js';
import { formatDateTime } from '../../lib/format-date.js';
import {
  auditLogApi,
  auditLogFilterQueryKey,
} from '../../lib/api/audit-log.js';
import { Badge } from '../../components/ui/badge.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Select } from '../../components/ui/select.js';
import { Input } from '../../components/ui/input.js';

// Known prefixes. "" → no filter (All events). The user can pick "Custom…"
// to fall through to a free-text input when a new namespace appears.
const KNOWN_PREFIXES: readonly { value: string; label: string }[] = [
  { value: '', label: 'All events' },
  { value: 'billing.', label: 'Billing' },
  { value: 'identity.', label: 'Identity' },
  { value: 'api_keys.', label: 'API keys' },
  { value: 'agents.', label: 'Agents' },
  { value: 'kb.', label: 'Knowledge base' },
  { value: 'subscription.', label: 'Subscription' },
  { value: 'reconciliation.', label: 'Reconciliation' },
  { value: 'prompt_injection.', label: 'Prompt injection' },
];

const NAMESPACE_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'muted'> = {
  billing: 'success',
  identity: 'default',
  api_keys: 'warning',
  agents: 'secondary',
  kb: 'muted',
  subscription: 'success',
  reconciliation: 'muted',
  prompt_injection: 'destructive',
};

function namespaceFor(action: string): string {
  const idx = action.indexOf('.');
  return idx === -1 ? action : action.slice(0, idx);
}

export function AuditLogPage() {
  const api = useApiClient();
  // `selection` tracks the dropdown value; `custom` holds the free-text input
  // shown when the dropdown is '__custom__'. `effectiveAction` is what we
  // actually pass to the API + queryKey.
  const [selection, setSelection] = useState<string>('');
  const [custom, setCustom] = useState<string>('');
  const effectiveAction = selection === '__custom__' ? custom.trim() : selection;

  const list = useQuery({
    queryKey: auditLogFilterQueryKey(effectiveAction || undefined),
    queryFn: () =>
      auditLogApi.list(
        api,
        effectiveAction.length > 0 ? { action: effectiveAction } : {},
      ),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        description="Read-only trail of privileged events — key revocations, plan changes, webhook deliveries, prompt-injection flags. Append-only by design."
      />

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="text-sm">Filter</CardTitle>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select
              aria-label="Filter by action namespace"
              className="sm:max-w-xs"
              value={selection}
              onChange={(e) => {
                setSelection(e.currentTarget.value);
                if (e.currentTarget.value !== '__custom__') setCustom('');
              }}
            >
              {KNOWN_PREFIXES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value="__custom__">Custom…</option>
            </Select>
            {selection === '__custom__' && (
              <Input
                aria-label="Custom action prefix"
                placeholder="e.g. billing.invoice."
                value={custom}
                maxLength={64}
                onChange={(e) => { setCustom(e.currentTarget.value); }}
                className="sm:max-w-xs"
              />
            )}
          </div>
        </CardHeader>
      </Card>

      {list.isLoading && <RowsSkeleton />}
      {list.isError &&
        (list.error instanceof ApiError && list.error.status === 403 ? (
          // Dedicated copy for 403 — tells the operator the *fix* (mint a key
          // with audit:read or use the dashboard user account) instead of a
          // generic HTTP error. Worth the extra branch.
          <Card>
            <CardHeader>
              <CardTitle>Not authorised</CardTitle>
              <CardDescription>
                Your API key is missing the <code>audit:read</code> scope. Ask an admin to mint a
                key with it or switch to the dashboard user account.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <ErrorRetry
            title="Could not load audit log"
            error={list.error}
            onRetry={() => void list.refetch()}
          />
        ))}
      {list.data?.entries.length === 0 && (
        <EmptyState
          icon={ScrollText}
          title="No audit entries"
          description={
            effectiveAction.length > 0
              ? 'Nothing matches this filter yet. Try a broader namespace or clear it.'
              : 'Privileged events (key revocations, plan changes, webhooks) will land here as they happen.'
          }
        />
      )}
      {list.data && list.data.entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" aria-hidden />
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Namespace</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.entries.map((e) => (
                  <EntryRow key={e.id} entry={e} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface EntryRowProps {
  entry: AuditLogEntry;
}

function EntryRow({ entry }: EntryRowProps) {
  const [expanded, setExpanded] = useState(false);
  const metaPretty = useMemo(() => JSON.stringify(entry.meta, null, 2), [entry.meta]);
  const hasMeta = Object.keys(entry.meta).length > 0;
  const Icon = expanded ? ChevronDown : ChevronRight;
  const namespace = namespaceFor(entry.action);
  const variant = NAMESPACE_VARIANT[namespace] ?? 'muted';

  return (
    <>
      <TableRow
        onClick={() => {
          if (hasMeta) setExpanded((v) => !v);
        }}
        className={hasMeta ? 'cursor-pointer' : ''}
        aria-expanded={hasMeta ? expanded : undefined}
      >
        <TableCell className="w-8">
          {hasMeta ? (
            <button
              type="button"
              aria-label={expanded ? 'Collapse metadata' : 'Expand metadata'}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              <Icon className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(entry.ts)}
        </TableCell>
        <TableCell>
          <Badge variant={variant} className="font-mono text-[11px] capitalize">
            {namespace.replace(/_/g, ' ')}
          </Badge>
        </TableCell>
        <TableCell className="font-mono text-xs">{entry.action}</TableCell>
        <TableCell className="font-mono text-xs">
          {entry.actorId ?? (
            <span className="italic text-muted-foreground">system</span>
          )}
        </TableCell>
        <TableCell className="max-w-xs truncate font-mono text-xs" title={entry.target ?? ''}>
          {entry.target ?? <span className="italic text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {entry.ip ?? '—'}
        </TableCell>
      </TableRow>
      {expanded && hasMeta && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={7} className="p-4">
            <pre className="whitespace-pre-wrap break-words text-xs">{metaPretty}</pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function RowsSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="grid grid-cols-6 items-center gap-4 px-4 py-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="h-4" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

